import React, { useState } from 'react';
import { StepperPage } from '../../Stepper';
import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import useVgsCollect from '../../VgsInput/hooks/useVgsCollect';
import { BUSINESS_STRUCTURES, BUSINESS_TYPES } from '../Kyb/businessTypes';
import { STATES } from '../../../const';
import { Consumer, StripeEnv } from '../../../util/enums';
import useFieldRevealTracker from '../../../hooks/useFieldRevealTracker';
import { FieldError } from '../../VgsInput/field';
import { ShowPayable, StripeMetadataInput, SuccessData } from '../../VaultForm';
import ErrorMessage from '../../ErrorMessage';
import BankAccountManagerInsights from '../../BankAccountManagerInsights';
import { useKybStep } from './useKybStep';
import { useKycStep } from './useKycStep';

const defaultBusinessType = BUSINESS_TYPES[0].value;

export const useInsightsSteps = ({
  org,
  payableOrg,
  currentUserId,
  provider,
  userKyc,
  consumer = Consumer.RAISE,
  onSuccess,
  onRevealSuccess,
  onRevealFail,
  token,
  raiseConfig,
  showSavePayableCheckbox,
  financialAccountRoots,
  statementDescriptor,
  metadata,
  stripeEnv,
  fundraiserId,
  fundraiserSlug,
  onBankAccountWidgetFinishClick,
  onBankAccountWidgetSkipClick,
  showSubmitForReviewButton,
}: {
  org?: OrgNode;
  payableOrg?: OrgNode;
  currentUserId?: string;
  userKyc?: Kyc;
  provider: VaultProvider;
  consumer: Consumer;
  onSuccess: (res: SuccessData) => void;
  onRevealSuccess: () => void;
  onRevealFail: () => void;
  token: string;
  raiseConfig?: {
    raiseCampaignId?: string;
    raiseUserId?: string;
  };
  showSavePayableCheckbox: ShowPayable;
  financialAccountRoots: FinancialAccountNode[];
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  stripeEnv: StripeEnv;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  onBankAccountWidgetFinishClick?: () => void;
  onBankAccountWidgetSkipClick?: () => void;
  showSubmitForReviewButton: boolean;
}): StepperPage[] => {
  const form = useVgsCollect<{
    vaultCreateKybKycSubmit: VaultFormMutationResponse;
  }>();
  const onFieldRevealSuccess = useFieldRevealTracker(onRevealSuccess, [
    'kyb',
    'kyc',
  ]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [successSubmissionStripeAccount, setSuccessSubmissionStripeAccount] =
    useState<string>();

  const [kybBusinessStructure, setKybBusinessStructure] = useState<string>(
    BUSINESS_STRUCTURES[defaultBusinessType][0].value,
  );
  const [kybBusinessType, setKybBusinessType] =
    useState<string>(defaultBusinessType);
  const [kybAddressRegion, setKybAddressRegion] = useState(STATES[0].value);
  const [hasBankAccount, setHasBankAccount] = useState<boolean>(false);

  const kybStep = useKybStep({
    org,
    onBusinessTypeChange: setKybBusinessType,
    onBusinessStructureChange: setKybBusinessStructure,
    onAddressRegionChange: setKybAddressRegion,
    form,
    onRevealSuccess: onFieldRevealSuccess('kyb'),
    onRevealFail,
    fieldErrors,
    token,
    raiseConfig,
    disabled: !showSubmitForReviewButton,
  });

  const kycStep = useKycStep({
    businessStructure: kybBusinessStructure,
    form,
    showSavePayableCheckbox,
    payableOrg,
    fieldErrors,
    userKyc,
    token,
    onRevealFail,
    onRevealSuccess: onFieldRevealSuccess('kyc'),
    setFieldErrors,
    setErrorMessage,
    org,
    currentUserId,
    provider,
    consumer,
    kybBusinessStructure,
    kybBusinessType,
    kybAddressRegion,
    financialAccountRoots,
    statementDescriptor,
    metadata,
    stripeEnv,
    fundraiserId,
    fundraiserSlug,
    setSuccessSubmissionStripeAccount,
    onSuccess,
  });

  if (consumer !== Consumer.INSIGHTS) {
    return [];
  }

  const bankAccountManagerStep = {
    component:
      errorMessage !== undefined ? (
        <ErrorMessage
          message={errorMessage}
          showThankYou={fieldErrors.length === 0}
        />
      ) : (
        <BankAccountManagerInsights
          // successSubmissionStripeAccount is checked to exist above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          stripeConnectAccountId={successSubmissionStripeAccount!}
          setHasBankAccount={setHasBankAccount}
          setError={setErrorMessage}
        />
      ),
    isValid: hasBankAccount && !errorMessage,
    navigation: {
      showBack: fieldErrors.length > 0,
      showForward: false,
      onSkip:
        !hasBankAccount && !errorMessage
          ? (): void => {
              if (onBankAccountWidgetSkipClick !== undefined) {
                onBankAccountWidgetSkipClick();
              }
            }
          : undefined,
      onFinish:
        hasBankAccount && !errorMessage
          ? (): void => {
              if (onBankAccountWidgetFinishClick) {
                onBankAccountWidgetFinishClick();
              }
            }
          : undefined,
      finishIsValid: hasBankAccount,
      showSteps: true,
    },
    srName:
      errorMessage !== undefined
        ? 'Create KYC/KYB Error'
        : 'Create KYC/KYB Bank Account Manager',
    shouldRender: true,
  };

  const steps = [kybStep, kycStep, bankAccountManagerStep];
  if (steps.every((s) => s !== null)) {
    return steps as StepperPage[];
  } else {
    return [];
  }
};
